import { useEffect, useMemo, useState } from "react";
import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import { mutate } from "swr";
import { Button } from "../../../../components/common/button";
import { Dialog } from "../../../../components/common/dialog";
import { Input } from "../../../../components/common/input";
import { SimpleTable } from "../../../../components/common/simple-table";
import { ShimmerLoader } from "../../../../components/common/shimmer";
import { Text } from "../../../../components/common/typography";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

export interface CrudRow {
  id: number;
  name: string;
}

export interface ExtraField<T extends CrudRow> {
  key: string;
  label: string;
  /** Render a column for this field. */
  render: (row: T) => string;
  /** Render the form field. Receives current value + change handler. */
  renderInput: (
    value: string,
    onChange: (next: string) => void,
  ) => React.ReactNode;
  /** Map the form value back to the API payload. */
  toPayload: (value: string) => unknown;
  /** Initial form value for create. */
  defaultValue: string;
  /** Initial form value when editing an existing row. */
  fromRow: (row: T) => string;
}

export interface CatalogCrudSectionProps<T extends CrudRow> {
  title: string;
  swrKey: string;
  fetcher: () => Promise<T[]>;
  /** Optional. When omitted, the "Add" button is hidden — section becomes read+edit+delete only. */
  create?: (input: Record<string, unknown>) => Promise<unknown>;
  update: (id: number, input: Record<string, unknown>) => Promise<unknown>;
  remove: (id: number) => Promise<void>;
  extraFields?: ExtraField<T>[];
}

export function CatalogCrudSection<T extends CrudRow>(
  props: CatalogCrudSectionProps<T>,
) {
  const {
    title,
    swrKey,
    fetcher,
    create,
    update,
    remove,
    extraFields = [],
  } = props;

  const [rows, setRows] = useState<T[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [editing, setEditing] = useState<T | null>(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [extras, setExtras] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<T | null>(null);

  const reload = async () => {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetcher();
      setRows(data);
    } catch (e) {
      setLoadError((e as Error)?.message ?? "Failed to load");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void reload();
  }, [swrKey]);

  const openCreate = () => {
    setCreating(true);
    setEditing(null);
    setName("");
    setExtras(
      Object.fromEntries(extraFields.map((f) => [f.key, f.defaultValue])),
    );
  };

  const openEdit = (row: T) => {
    setEditing(row);
    setCreating(false);
    setName(row.name);
    setExtras(
      Object.fromEntries(extraFields.map((f) => [f.key, f.fromRow(row)])),
    );
  };

  const close = () => {
    setEditing(null);
    setCreating(false);
  };

  const isOpen = creating || editing !== null;

  const submit = async () => {
    if (!name.trim()) return;
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = { name: name.trim() };
      for (const f of extraFields) {
        const v = f.toPayload(extras[f.key] ?? f.defaultValue);
        if (v !== undefined && v !== "") payload[f.key] = v;
      }
      if (editing) {
        await update(editing.id, payload);
        showSuccessToast(`${title.replace(/s$/, "")} updated`);
      } else if (create) {
        await create(payload);
        showSuccessToast(`${title.replace(/s$/, "")} created`);
      }
      close();
      await reload();
      await mutate(swrKey);
    } catch (e) {
      showErrorToast((e as Error)?.message ?? "Save failed", e);
    } finally {
      setSubmitting(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await remove(deleteTarget.id);
      showSuccessToast(`${title.replace(/s$/, "")} archived`);
      setDeleteTarget(null);
      await reload();
      await mutate(swrKey);
    } catch (e) {
      showErrorToast((e as Error)?.message ?? "Delete failed", e);
    }
  };

  const columns = useMemo(() => {
    const cols: {
      key: string;
      label: string;
      className?: string;
      render: (row: T) => React.ReactNode;
    }[] = [
      {
        key: "id",
        label: "ID",
        className: "w-20",
        render: (r) => <Text variant="body">{r.id}</Text>,
      },
      {
        key: "name",
        label: "Name",
        className: "flex-1",
        render: (r) => <Text variant="body">{r.name}</Text>,
      },
      ...extraFields.map((f) => ({
        key: f.key,
        label: f.label,
        className: "flex-1",
        render: (r: T) => <Text variant="body">{f.render(r)}</Text>,
      })),
    ];
    cols.push({
      key: "actions",
      label: "",
      className: "w-32 flex justify-end gap-2",
      render: (r) => (
        <div className="flex gap-2">
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<IconEdit size={16} />}
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="xs"
            leftIcon={<IconTrash size={16} />}
            onClick={() => setDeleteTarget(r)}
          >
            Delete
          </Button>
        </div>
      ),
    });
    return cols;
  }, [extraFields]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <Text variant="bodyBold" tone="dark">
          {title}
          {rows ? ` (${rows.length})` : ""}
        </Text>
        {create && (
          <Button
            variant="primary"
            size="sm"
            leftIcon={<IconPlus size={16} />}
            onClick={openCreate}
          >
            Add new
          </Button>
        )}
      </div>

      {isLoading && (
        <ShimmerLoader message={`Loading ${title.toLowerCase()}…`} lines={4} />
      )}
      {loadError && (
        <Text variant="body" tone="body" className="text-accentError">
          {loadError}
        </Text>
      )}

      {rows && rows.length > 0 && (
        <SimpleTable
          columns={columns}
          data={rows}
          keyExtractor={(r) => String(r.id)}
        />
      )}

      {rows && rows.length === 0 && !isLoading && (
        <Text variant="body" tone="muted">
          No entries yet — click "Add new" to create one.
        </Text>
      )}

      <Dialog
        open={isOpen}
        setOpen={(o) => {
          if (!o) close();
        }}
        title={editing ? `Edit ${title.replace(/s$/, "")}` : `New ${title.replace(/s$/, "")}`}
        showButtons
        buttonText={submitting ? "Saving…" : "Save"}
        onConfirm={submit}
        cancelText="Cancel"
        showCloseButton
        content={
          <div className="flex flex-col gap-3 py-2">
            <Input
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoFocus
            />
            {extraFields.map((f) => (
              <div key={f.key}>
                {f.renderInput(extras[f.key] ?? f.defaultValue, (next) =>
                  setExtras((prev) => ({ ...prev, [f.key]: next })),
                )}
              </div>
            ))}
          </div>
        }
      />

      <Dialog
        open={deleteTarget !== null}
        setOpen={(o) => {
          if (!o) setDeleteTarget(null);
        }}
        title={`Delete ${deleteTarget?.name ?? ""}?`}
        text="The record will be removed from Odoo. Existing products that already use it are unaffected."
        showButtons
        buttonText="Delete"
        onConfirm={confirmDelete}
        cancelText="Cancel"
      />
    </div>
  );
}
